import { useState, useEffect } from "react";
import { FunnelIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { SelectListItem, SelectList } from "../../../../components/input/SelectList";
import { useGetScoreCategoriesQuery, useGetGenresQuery } from "../../../lookup/lookupApiSlice";

export interface ProgressChartFilter {
	trait: number;
	genre: number;
	year: number;
	type: number;
}

export const initialFilter: ProgressChartFilter = {
	trait: 0,
	genre: 0,
	year: 0,
	type: 0,
};

export function ProgressChartFilters(props: {
	filter: ProgressChartFilter;
	schoolYears: SelectListItem[];
	draftTypes: SelectListItem[];
	onChange: (filter: ProgressChartFilter) => void;
}) {
	const [showExtraFilters, setShowExtraFilters] = useState(false);
	const [traitFilters, setTraitFilters] = useState<SelectListItem[]>([]);
	const [genreFilters, setGenreFilters] = useState<SelectListItem[]>([]);

	const traitsResult = useGetScoreCategoriesQuery();
	const traits = traitsResult.data;

	const genresResult = useGetGenresQuery();
	const genres = genresResult.data;

	useEffect(() => {
		if (!traits) return;
		const filters = traits
			.filter((t) => t.isEssayTrait)
			.map(
				(t) =>
					({
						value: t.scoreCategoryId,
						text: t.name,
					}) as SelectListItem
			);
		filters.unshift({ value: 0, text: "Total Score" } as SelectListItem);
		setTraitFilters(filters);
	}, [traits]);

	useEffect(() => {
		if (!genres) return;
		const filters = genres
			.filter((t) => t.isEssayGenre)
			.map(
				(t) =>
					({
						value: t.genreId,
						text: t.name,
					}) as SelectListItem
			);
		filters.unshift({ value: 0, text: "All Genres" } as SelectListItem);
		setGenreFilters(filters);
	}, [genres]);

	function handleExtraFiltersClick() {
		setShowExtraFilters(!showExtraFilters);
	}

	return (
		<div className="mt-2 flex items-center flex-wrap">
			<SelectList
				id="traitFilter"
				selectListItems={traitFilters}
				value={props.filter.trait}
				className={"ml-2 mb-2 basis-1/5 flex-shrink-0"}
				onChange={(value) => props.onChange({ ...props.filter, trait: +value })}
			/>
			<SelectList
				id="genreFilter"
				selectListItems={genreFilters}
				value={props.filter.genre}
				className={"ml-2 mb-2 basis-1/5 flex-shrink-0"}
				onChange={(value) => props.onChange({ ...props.filter, genre: +value })}
			/>
			{showExtraFilters && (
				<>
					<SelectList
						id="yearFilter"
						className="ml-2 mb-2 basis-[12.5%]"
						selectListItems={props.schoolYears}
						value={props.filter.year}
						onChange={(value) => props.onChange({ ...props.filter, year: +value })}
					/>
					<SelectList
						id="draftTypeFilter"
						className="ml-2 mb-2 w-1/6"
						selectListItems={props.draftTypes}
						value={props.filter.type}
						onChange={(value) => props.onChange({ ...props.filter, type: +value })}
					/>
				</>
			)}

			<div
				className="inline-block ml-4 mb-2 cursor-pointer"
				onClick={handleExtraFiltersClick}>
				{!showExtraFilters && (
					<span>
						<FunnelIcon
							className="inline-block mr-1.5 size-4 shrink-0 text-gray-400"
							aria-hidden="true"
						/>
						Advanced Filtering
					</span>
				)}
				{showExtraFilters && (
					<span>
						<XCircleIcon
							className="inline-block mr-1.5 size-4 shrink-0 text-gray-400"
							aria-hidden="true"
						/>
						Hide Advanced Filters
					</span>
				)}
			</div>
		</div>
	);
}
